import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import CompanyFormDialog from './CompanyFormDialog';
import BranchFormDialog from './BranchFormDialog';
import type { BranchFormValues } from './BranchFormDialog';
import type { BranchDto, CompanyDto, CreateBranchRequest, UpdateBranchRequest, UpdateCompanyRequest } from '@/types/organization';

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function CompanyMaster() {
  const { t } = useTranslation('companyMaster');
  const queryClient = useQueryClient();

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => (await apiClient.get<CompanyDto[]>('/companies')).data,
  });
  const company = companies[0] as CompanyDto | undefined;

  const { data: branches = [], isLoading: branchesLoading } = useQuery({
    queryKey: ['branches', company?.id],
    queryFn: async () => (await apiClient.get<BranchDto[]>('/branches', { params: { companyId: company?.id } })).data,
    enabled: Boolean(company),
  });

  const { data: processConfiguration } = useProcessConfiguration();
  const maxBranches = processConfiguration?.maxBranches ?? null;

  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<BranchDto | null>(null);
  const [confirmDeleteBranch, setConfirmDeleteBranch] = useState<BranchDto | null>(null);

  const invalidateBranches = () => queryClient.invalidateQueries({ queryKey: ['branches', company?.id] });

  const saveCompanyMutation = useMutation({
    mutationFn: (values: UpdateCompanyRequest) => apiClient.put(`/companies/${company!.id}`, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      setCompanyDialogOpen(false);
    },
  });

  const saveBranchMutation = useMutation({
    mutationFn: (values: BranchFormValues) => {
      if (editingBranch) {
        const payload: UpdateBranchRequest = { ...values, managerUserId: editingBranch.managerUserId };
        return apiClient.put(`/branches/${editingBranch.id}`, payload);
      }
      const payload: CreateBranchRequest = { ...values, companyId: company!.id, managerUserId: null };
      return apiClient.post('/branches', payload);
    },
    onSuccess: () => {
      invalidateBranches();
      setBranchDialogOpen(false);
      setEditingBranch(null);
    },
  });

  const deleteBranchMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/branches/${id}`),
    onSuccess: () => {
      invalidateBranches();
      setConfirmDeleteBranch(null);
    },
  });

  const columns: DataTableColumn<BranchDto>[] = [
    { field: 'branchCode', headerName: t('table.code') },
    { field: 'name', headerName: t('table.name') },
    { field: 'address', headerName: t('table.address'), render: (r) => r.address ?? '—' },
    { field: 'managerUserName', headerName: t('table.manager'), render: (r) => r.managerUserName ?? '—' },
    {
      field: 'warehouseTags',
      headerName: t('table.warehouses'),
      render: (r) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {r.warehouseTags.length === 0 ? '—' : r.warehouseTags.map((w) => <Chip key={w} label={w} size="small" />)}
        </Stack>
      ),
    },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) => (
        <IfPermitted permission={PERMISSIONS.COMPANY_MANAGE}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Button
              size="small"
              onClick={() => {
                setEditingBranch(r);
                setBranchDialogOpen(true);
              }}
            >
              Edit
            </Button>
            <Button size="small" color="error" onClick={() => setConfirmDeleteBranch(r)}>
              Delete
            </Button>
          </Stack>
        </IfPermitted>
      ),
    },
  ];

  if (!company) return null;

  return (
    <>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      <GridContainer spacing={2} columns={12}>
        <GridItem xs={12} md={8}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h4">{company.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    CIN: {company.cin ?? '—'} · GSTIN: {company.gstin ?? '—'}
                  </Typography>
                </Box>
                <IfPermitted permission={PERMISSIONS.COMPANY_MANAGE}>
                  <Button startIcon={<EditIcon />} onClick={() => setCompanyDialogOpen(true)}>
                    {t('editCompany')}
                  </Button>
                </IfPermitted>
              </Stack>
              <GridContainer spacing={2} columns={2} >
                <GridItem xs={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t('form.email')}
                  </Typography>
                  <Typography variant="body1">{company.email ?? '—'}</Typography>
                </GridItem>
                <GridItem xs={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t('form.website')}
                  </Typography>
                  <Typography variant="body1">{company.website ?? '—'}</Typography>
                </GridItem>
                <GridItem xs={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t('form.parentCompanyName')}
                  </Typography>
                  <Typography variant="body1">{company.parentCompanyName ?? '—'}</Typography>
                </GridItem>
                <GridItem xs={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t('form.industrySector')}
                  </Typography>
                  <Typography variant="body1">{company.industrySector ?? '—'}</Typography>
                </GridItem>
              </GridContainer>
            </CardContent>
          </Card>
        </GridItem>

        <GridItem xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 2 }}>
                {t('financial')}
              </Typography>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">{t('fiscalYear')}</Typography>
                  <Typography fontWeight={600}>
                    {MONTH_NAMES[company.fiscalYearStartMonth]} {company.fiscalYearStartDay}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">{t('currency')}</Typography>
                  <Typography fontWeight={600}>{company.currency}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">{t('taxSystem')}</Typography>
                  <Typography fontWeight={600}>{company.taxSystem}</Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </GridItem>
      </GridContainer>

      <Box sx={{ mt: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h4">{t('branches')}</Typography>
            <Chip
              size="small"
              variant="outlined"
              color={maxBranches !== null && branches.length >= maxBranches ? 'warning' : 'default'}
              label={maxBranches !== null ? t('branchLimitLabel', { count: branches.length, limit: maxBranches }) : t('branchLimitUnlimitedLabel', { count: branches.length })}
            />
          </Stack>
          <IfPermitted permission={PERMISSIONS.COMPANY_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingBranch(null);
                setBranchDialogOpen(true);
              }}
            >
              {t('addBranch')}
            </Button>
          </IfPermitted>
        </Stack>

        <DataTable columns={columns} rows={branches} loading={branchesLoading} searchable getRowId={(r) => r.id} exportFileName="branches" />
      </Box>

      <CompanyFormDialog
        open={companyDialogOpen}
        company={company}
        onClose={() => setCompanyDialogOpen(false)}
        onSubmit={(values) => saveCompanyMutation.mutate(values)}
        loading={saveCompanyMutation.isPending}
      />

      <BranchFormDialog
        open={branchDialogOpen}
        branch={editingBranch}
        onClose={() => {
          setBranchDialogOpen(false);
          setEditingBranch(null);
        }}
        onSubmit={(values) => saveBranchMutation.mutate(values)}
        loading={saveBranchMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(confirmDeleteBranch)}
        message={t('deleteBranch.message')}
        loading={deleteBranchMutation.isPending}
        onConfirm={() => confirmDeleteBranch && deleteBranchMutation.mutate(confirmDeleteBranch.id)}
        onClose={() => setConfirmDeleteBranch(null)}
      />
    </>
  );
}
